'use client';

import { useCallback, useMemo, useState } from 'react';

import type { SupabaseClient } from '@supabase/supabase-js';

import { recordConsent } from '../consent';
import { getConsentItems } from '../consent-configs';
import type { ConsentItem } from '../types';

interface UseConsentFormReturn {
  /** The consent items for the current jurisdiction and side. */
  items: ConsentItem[];

  /** Current checkbox state, keyed by item.id. */
  values: Record<string, boolean>;

  /** Validation errors keyed by item.id. Empty when valid. */
  errors: Record<string, string>;

  /** Whether all required consent items are checked. */
  isValid: boolean;

  /** Toggle a single consent checkbox. */
  setConsent: (id: string, checked: boolean) => void;

  /** Validate all required checkboxes. Returns true if valid. */
  validate: () => boolean;

  /**
   * Create consent_records for all checked items.
   * Inserts one row per checked consent item via the recordConsent function.
   */
  createConsentRecords: (
    adminClient: SupabaseClient,
    leadId: string,
    accountId: string,
    metadata?: {
      ipAddress?: string;
      userAgent?: string;
    },
  ) => Promise<void>;
}

/**
 * Hook for managing granular consent form state.
 *
 * Gets consent items from getConsentItems(), manages checkbox state,
 * validates required checkboxes, and provides a function to create
 * consent_records in the database.
 *
 * @param jurisdiction - The compliance jurisdiction (e.g. "ch-dsg-health")
 * @param side - Whether this is a supply-side or demand-side form
 * @returns Consent state, validation, and record creation functions
 */
export function useConsentForm(
  jurisdiction: string,
  side: 'supply' | 'demand',
): UseConsentFormReturn {
  const items = useMemo(
    () => getConsentItems(jurisdiction, side),
    [jurisdiction, side],
  );

  const [values, setValues] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const item of items) {
      initial[item.id] = false;
    }
    return initial;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const setConsent = useCallback((id: string, checked: boolean) => {
    setValues((prev: Record<string, boolean>) => ({ ...prev, [id]: checked }));
    // Clear error for this item when the user checks it
    if (checked) {
      setErrors((prev: Record<string, string>) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    let valid = true;

    for (const item of items) {
      if (item.required && !values[item.id]) {
        newErrors[item.id] = 'Diese Einwilligung ist erforderlich.';
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  }, [items, values]);

  const isValid = useMemo(() => {
    return items.every(
      (item: ConsentItem) => !item.required || values[item.id],
    );
  }, [items, values]);

  const createConsentRecords = useCallback(
    async (
      adminClient: SupabaseClient,
      leadId: string,
      accountId: string,
      metadata?: {
        ipAddress?: string;
        userAgent?: string;
      },
    ): Promise<void> => {
      const checkedItems = items.filter((item: ConsentItem) => values[item.id]);

      await Promise.all(
        checkedItems.map((item: ConsentItem) =>
          recordConsent(adminClient, {
            leadId,
            accountId,
            consentType: 'explicit',
            jurisdiction: jurisdiction as
              | 'us-tcpa'
              | 'eu-gdpr'
              | 'ch-dsg'
              | 'ch-dsg-health',
            channel: (item.channel ?? 'email') as
              | 'email'
              | 'sms'
              | 'whatsapp'
              | 'phone'
              | 'instagram',
            consentText: item.text,
            ipAddress: metadata?.ipAddress,
            userAgent: metadata?.userAgent,
            metadata: {
              consentItemId: item.id,
              consentPurpose: item.purpose,
            },
          }),
        ),
      );
    },
    [items, values, jurisdiction],
  );

  return {
    items,
    values,
    errors,
    isValid,
    setConsent,
    validate,
    createConsentRecords,
  };
}
